import { useWallet } from '@solana/wallet-adapter-react'
import { Connection, PublicKey } from '@solana/web3.js'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useLocalStorage } from './useLocalStorageState'
import { REFERRER_KEY } from 'utils/constants'
import store from 'stores/store'
import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  TOKEN_PROGRAM_ID,
} from '@solana/spl-token'
import { useCallback, useMemo } from 'react'
import { useReferrerFee } from '@components/partners/useReferrerFee'
import { milliseconds } from 'date-fns'

const fetchReferrerVault = async (
  connection: Connection,
  localReferrer: string,
  mint: string,
  tokenProgramId?: string,
) => {
  const [tokenAccountAddress] = PublicKey.findProgramAddressSync(
    [
      new PublicKey(localReferrer).toBuffer(),
      tokenProgramId
        ? new PublicKey(tokenProgramId).toBuffer()
        : TOKEN_PROGRAM_ID.toBuffer(),
      new PublicKey(mint).toBuffer(),
    ],
    ASSOCIATED_TOKEN_PROGRAM_ID,
  )

  try {
    const account = await connection.getAccountInfo(tokenAccountAddress)
    return {
      account,
      tokenAccountPk: tokenAccountAddress,
      referrer: localReferrer,
    }
  } catch (e) {
    console.error('failed to fetch referrer', e)
    return {
      account: null,
      tokenAccountPk: tokenAccountAddress,
      referrer: localReferrer,
    }
  }
}

const makeGetReferrerQueryKey = (
  localReferrer: string | null | undefined,
  mint: string | undefined,
) => ['get-referrer', localReferrer || '', mint || '']

export const useReferrerVault = (mint?: string, tokenProgramId?: string) => {
  const { publicKey } = useWallet()
  const [localReferrer] = useLocalStorage(REFERRER_KEY)
  const { referrerFee, loading: loadingReferrerFee } = useReferrerFee()

  const connection = store((s) => s.connection)
  const { data: vault, isInitialLoading: loading } = useQuery({
    queryKey: makeGetReferrerQueryKey(localReferrer?.wallet, mint),
    queryFn: () => {
      if (!localReferrer || !mint) {
        throw new Error(
          `Missing local referrer or mint in useReferrerVault: localReferrer=${localReferrer}, mint=${mint}`,
        )
      }
      return fetchReferrerVault(
        connection,
        localReferrer.wallet,
        mint,
        tokenProgramId,
      )
    },
    cacheTime: milliseconds({ minutes: 15 }),
    staleTime: milliseconds({ minutes: 10 }),
    retry: 2,
    refetchOnWindowFocus: false,
    enabled: !!(localReferrer?.wallet && publicKey && mint && connection),
  })

  const referrerVault = useMemo(() => {
    if (!vault || loadingReferrerFee) return undefined
    return {
      ...vault,
      referrerFee,
    }
  }, [vault, referrerFee, loadingReferrerFee])

  return { vault: referrerVault, loading }
}

export function useInvalidateReferrerVault() {
  const queryClient = useQueryClient()
  const [localReferrer] = useLocalStorage(REFERRER_KEY)

  return useCallback(
    async (mint: string) => {
      if (!localReferrer?.wallet) return

      return queryClient.invalidateQueries({
        queryKey: makeGetReferrerQueryKey(localReferrer.wallet, mint),
      })
    },
    [localReferrer, queryClient],
  )
}
